import {Fragment, useRef, useState, useEffect} from "react";
import uuid from "react-uuid";
import { formEl } from "../constants";
import { QuestionTypes } from "../../../types/Question";
import Paper from "../../ui/customs/Paper";
import Select from "../../form/Select";
import MyEditor5 from "../../form/input/MyEditor5";
import {Copy, GripVertical, Trash2, ChevronUp, ChevronDown, Shuffle, RotateCcw} from "lucide-react";

type Option = {
    id: string;
    uid: string;
    title: string;
    isCorrect: number;
};

type DragAndDropInputProps = {
    item: {
        id: string;
        uid: string;
        title: string;
        value: string | null;
        required: boolean;
        type: string;
        answers?: Option[];
        displayAnswers?: Option[];
    };
    currentType: QuestionTypes;
    handleValue: (id: string, newValue: string, type: QuestionTypes) => void;
    deleteEl: (uuid: {id?: string, uid: string}) => void;
    handleRequired: (id: string) => void;
    handleElType: (id: string, value: QuestionTypes) => void;
    addOption: (id: string, option: Option) => void;
    handleOptionValues: (
        itemId: string,
        answer: { optionId: string; uid: string; optionVal: string; isCorrect: number },
        type: QuestionTypes
    ) => void;
    deleteOption: (itemId: string, uid: string, optionId: string) => void;
    duplicateElement: (id: string, type: QuestionTypes) => void;
    isSubQuestion?: boolean;
    updateAnswersOrder?: (itemId: string, newAnswers: Option[]) => void;
    updateViewOrder?: (itemId: string, displayAnswers: Option[]) => void;
};

const DragAndDropInput: React.FC<DragAndDropInputProps> = ({
                                                               item,
                                                               currentType,
                                                               handleValue,
                                                               deleteEl,
                                                               handleRequired,
                                                               handleElType,
                                                               addOption,
                                                               handleOptionValues,
                                                               deleteOption,
                                                               duplicateElement,
                                                               isSubQuestion,
                                                               updateAnswersOrder,
                                                               updateViewOrder,
                                                           }) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const displayAnswers = item.displayAnswers || null;

    const createNewOption = (id: string) => {
        const data: Option = {
            id: "",
            uid: uuid(),
            title: "",
            isCorrect: 0,
        };
        addOption(id, data);
        // const currentLength = item.answers.length;
        // const newOption = item.answers[currentLength - 1];
        // const updatedViewOrder = [...displayAnswers, newOption];
        // updateViewOrder?.(item.id, updatedViewOrder);
    };

    const deleteOptionF= (itemId: any, optUid: any, optId: any) => {
        deleteOption(itemId, optUid, optId);
        // const currentUids = new Set(item.answers.map(ans => ans.uid));
        // const updatedViewOrder = displayAnswers.filter(opt => currentUids.has(opt.uid));
        // updateViewOrder?.(item.id, updatedViewOrder);
    }

    const moveOption = (currentIndex: number, direction: 'up' | 'down') => {
        if (!item.answers) return;

        const newAnswers = [...item.answers];
        const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

        if (targetIndex < 0 || targetIndex >= newAnswers.length) return;

        [newAnswers[currentIndex], newAnswers[targetIndex]] = [newAnswers[targetIndex], newAnswers[currentIndex]];
        updateAnswersOrder?.(item.id, newAnswers);
    };

    const generateViewOrder = () => {
        if (!item.answers) return;

        const shuffled = [...item.answers].sort(() => Math.random() - 0.5);
        updateViewOrder?.(item.id, shuffled);
    };

    return (
        <Fragment>
            <Paper className="mb-4">
                <div className="flex justify-between items-center mb-4">
                    <GripVertical className="text-gray-400 cursor-move"/>
                    <div className="w-1/4">
                        {isSubQuestion ? (
                            <Select
                                defaultValue={String(currentType)}
                                onChange={(val) => handleElType(item.id, Number(val))}
                                options={formEl.filter((el) => !el.hasChildren)
                                    .map((el) => ({
                                        label: el.label,
                                        value: String(el.value),
                                    }))}
                            />
                        ) : (
                            <Select
                                defaultValue={String(currentType)}
                                onChange={(val) => handleElType(item.id, Number(val))}
                                options={formEl.map((el) => ({
                                    label: el.label,
                                    value: String(el.value),
                                }))}
                            />
                        )}
                    </div>
                </div>

                <div className="flex flex-col gap-3 mb-4 p-4">
                    <MyEditor5
                        content={item.title}
                        onBlur={(value) => handleValue(item.id, value, currentType)}
                    />

                    {/* Header với buttons quản lý thứ tự */}
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">Thứ tự chỉnh sửa (Admin):</span>
                        <div className="flex gap-2">
                            <button
                                className="flex items-center gap-2 px-3 py-1 text-sm rounded transition-colors bg-blue-100 text-blue-700 border border-blue-300 hover:bg-blue-200"
                                onClick={generateViewOrder}
                                disabled={!item.answers || item.answers.length < 2}
                                title="Tạo thứ tự ngẫu nhiên để hiển thị cho học viên"
                            >
                                <Shuffle size={14} />
                                Tạo thứ tự view
                            </button>
                        </div>
                    </div>

                    {/* Khối thứ tự admin - luôn hiển thị */}
                    {item.answers?.map((opt, index) => (
                        <div
                            key={opt.uid}
                            className="flex items-center space-x-3 border p-3 rounded bg-gray-50"
                        >
                            <div className="flex flex-col gap-1">
                                <button
                                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                    onClick={() => moveOption(index, 'up')}
                                    disabled={index === 0}
                                    title="Di chuyển lên"
                                >
                                    <ChevronUp size={16} />
                                </button>
                                <button
                                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                    onClick={() => moveOption(index, 'down')}
                                    disabled={index === (item.answers?.length || 0) - 1}
                                    title="Di chuyển xuống"
                                >
                                    <ChevronDown size={16} />
                                </button>
                            </div>

                            <div className="flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full text-sm font-medium">
                                {index + 1}
                            </div>

                            <MyEditor5
                                content={opt.title}
                                placeholder={`Option ${index + 1}`}
                                className={'flex-1 rounded text-sm'}
                                onBlur={(newValue) => {
                                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                                    timeoutRef.current = setTimeout(() => {
                                        handleOptionValues(item.id, {
                                            optionId: opt.id,
                                            uid: opt.uid,
                                            optionVal: newValue,
                                            isCorrect: opt.isCorrect,
                                        }, currentType);
                                    }, 800);
                                }}
                            />
                            <button
                                className="text-red-500 hover:text-red-700 p-1 rounded transition-colors"
                                onClick={() => deleteOptionF(item.id, opt.uid, opt.id)}
                                title="Xóa lựa chọn"
                            >
                                <Trash2 size={18} />
                            </button>
                        </div>
                    ))}

                    <button
                        className="mt-2 text-blue-600 hover:underline text-sm"
                        onClick={() => createNewOption(item.id)}
                    >
                        + Thêm lựa chọn
                    </button>

                    {/* Khối thứ tự view cho học viên - chỉ hiển thị khi có displayAnswers */}
                    {displayAnswers && (
                        <div className="mt-6 pt-4 border-t border-dashed border-gray-300">
                            <div className="flex justify-between items-center mb-3">
                                <span className="text-sm font-medium text-green-700">Thứ tự hiển thị cho học viên:</span>
                                <span className="text-xs text-gray-500 bg-green-50 px-2 py-1 rounded">Preview</span>
                            </div>

                            {displayAnswers.map((opt, index) => (
                                <div
                                    key={`view-${opt.uid}`}
                                    className="flex items-center space-x-3 border p-3 rounded bg-green-50 mb-2"
                                >
                                    <div className="flex items-center justify-center w-8 h-8 bg-green-200 rounded-full text-sm font-medium">
                                        {index + 1}
                                    </div>
                                    <input
                                        type="text"
                                        className="flex-grow border border-green-300 rounded px-3 py-2 text-sm bg-white"
                                        value={opt.title}
                                        readOnly
                                    />
                                    <span className="text-xs text-gray-500 w-20 text-center">Read-only</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="mt-4 flex items-center space-x-4 border-t pt-3">
                    <button
                        className="text-gray-500 hover:text-gray-800"
                        onClick={() => deleteEl({id: item.id, uid: item.uid})}
                        title="Xoá phần tử"
                    >
                        <Trash2 size={18} />
                    </button>
                    <button
                        className="text-gray-500 hover:text-gray-800"
                        onClick={() => duplicateElement(item.id, currentType)}
                        title="Nhân bản"
                    >
                        <Copy size={18} />
                    </button>

                    <label className="inline-flex items-center ml-auto text-sm text-gray-600">
                        <input
                            type="checkbox"
                            checked={item.required}
                            onChange={() => handleRequired(item.id)}
                            className="mr-2"
                        />
                        Bắt buộc
                    </label>
                </div>
            </Paper>
        </Fragment>
    );
};

export default DragAndDropInput;
